import { Lock, ShieldCheck, Clock, AlertTriangle } from "lucide-react";

const STATE = {
  verified: { icon: ShieldCheck, tone: "border-good bg-good/10 text-good", label: "Verified · standard ACH",
    note: "Deposits arrive on the normal ACH schedule, about 48 hours after payroll is authorized." },
  pending_verification: { icon: Clock, tone: "border-warn bg-warn/10", label: "Pending Verification",
    note: "Your administrator is confirming this account with two micro-deposits. Payroll can't use it until that's done." },
  unverified: { icon: AlertTriangle, tone: "border-warn bg-warn/10", label: "Awaiting authorization",
    note: "Your administrator entered this account and still needs to authorize it for payouts." },
};

/**
 * Admin-First mandate: the portal is a window, not a form. The employee sees
 * only the last four digits and can never type a routing or account number —
 * every write lives in the Admin's Employee Detail view.
 */
export const ManualBankReadOnly = ({ me }) => {
  const acct4 = me?.bank_account_last4 || "";
  const routing4 = me?.bank_routing_last4 || "";
  const onFile = Boolean(acct4 || routing4 || me?.bank_routing_on_file);
  const status = me?.plaid_verified ? "verified" : (me?.bank_verification?.status || (onFile ? "unverified" : null));
  const meta = STATE[status] || null;
  const Icon = meta?.icon || Lock;

  return (
    <div className="hard-card p-5" data-testid="portal-bank-readonly">
      <div className="overline mb-3 flex items-center gap-2"><Lock className="w-4 h-4" /> § BANK ON FILE · READ-ONLY</div>
      {!onFile ? (
        <div className="font-mono text-[11px] text-muted-foreground leading-relaxed" data-testid="portal-bank-none">
          No bank account on file yet. Your payroll administrator adds and verifies bank details for you —
          contact them to set up direct deposit.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="border-2 border-ink p-3">
              <div className="overline mb-1">Account number</div>
              <div className="font-mono text-lg font-black tracking-widest" data-testid="portal-account-masked">
                {acct4 ? `••••${acct4}` : "••••"}
              </div>
            </div>
            <div className="border-2 border-ink p-3">
              <div className="overline mb-1">Routing number</div>
              <div className="font-mono text-lg font-black tracking-widest" data-testid="portal-routing-masked">
                {routing4 ? `•••••${routing4}` : "•••••"}
              </div>
            </div>
          </div>
          {meta && (
            <div className={`border-2 p-3 flex items-start gap-2 ${meta.tone}`} data-testid="portal-bank-status">
              <Icon className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="font-mono text-[11px] leading-relaxed">
                <b className="uppercase tracking-widest text-[10px] block mb-0.5">{meta.label}</b>
                {meta.note}
              </div>
            </div>
          )}
          <div className="font-mono text-[10px] text-muted-foreground mt-3 leading-relaxed">
            Only your administrator can change these numbers, and every edit is recorded in the audit vault with
            their name and a timestamp. If a digit is wrong, ask them to update it — never share full account
            details over email or chat.
          </div>
        </>
      )}
    </div>
  );
};
